import { supabase } from './supabase';
import { getAvatarUrl, getStorefrontBannerUrl } from './profile';
import type { DiscoveryCategory, DiscoveryCreator } from '../data/discovery';
import type { DiscoverySelection } from './discovery-workshop';

export type DiscoveryStage = 'draft' | 'published';
export interface DiscoveryEntry {
  stage: DiscoveryStage;
  profile_id: string;
  status: DiscoverySelection['status'];
  display_kind: DiscoveryCreator['kind'];
  featured: boolean;
  sort_order: number;
  offer_kind: 'product' | 'event' | null;
  offer_id: string | null;
  category: DiscoveryCategory | null;
  editorial_location: string | null;
  editorial_summary: string | null;
  editorial_image_path: string | null;
}
interface ProfileRow {
  id: string; username: string; full_name: string | null; bio: string | null;
  role: string; avatar_path: string | null;
  hero_banner_path: string | null;
}
export interface DiscoveryContent {
  creators: DiscoveryCreator[];
  selections: DiscoverySelection[];
  entries: DiscoveryEntry[];
  publishedAt: string | null;
  revision: number;
}

const profileColumns = 'id,username,full_name,bio,role,avatar_path,hero_banner_path';
const entryColumns = 'stage,profile_id,status,display_kind,featured,sort_order,offer_kind,offer_id,category,editorial_location,editorial_summary,editorial_image_path';
export const DISCOVERY_CREATOR_PAGE_SIZE = 25;

export function getDiscoveryImageUrl(path: string | null): string | null {
  if (!path) return null;
  return supabase.storage.from('discovery-images').getPublicUrl(path).data.publicUrl;
}

export async function uploadDiscoveryImage(profileId: string, file: File): Promise<string> {
  const extensions: Record<string, string> = {
    'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp',
  };
  const extension = extensions[file.type];
  if (!extension) throw new Error('Use a JPG, PNG, or WebP photo.');
  if (file.size === 0 || file.size > 5 * 1024 * 1024) throw new Error('Photo must be 5 MB or smaller.');
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Please sign in again.');
  const path = `${user.id}/${profileId}/${crypto.randomUUID()}.${extension}`;
  const { data, error } = await supabase.storage.from('discovery-images')
    .upload(path, file, { cacheControl: '3600', contentType: file.type, upsert: false });
  assertQuery(error, 'Could not upload Discovery photo');
  return data.path;
}

function assertQuery(error: { message: string } | null, label: string) {
  if (error) throw new Error(`${label}: ${error.message}`);
}

function creatorFromProfile(profile: ProfileRow, entry?: DiscoveryEntry): DiscoveryCreator {
  const name = profile.full_name?.trim() || profile.username;
  const profileImageUrl = getStorefrontBannerUrl(profile.hero_banner_path) || getAvatarUrl(profile.avatar_path);
  return {
    id: profile.id, kind: 'creator', image: 'voice',
    imageUrl: getDiscoveryImageUrl(entry?.editorial_image_path ?? null) || profileImageUrl,
    profileImageUrl,
    name, handle: profile.username, initials: name.split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase(),
    category: entry?.category ?? null,
    location: entry?.editorial_location || '',
    bio: entry?.editorial_summary || profile.bio || '',
    colour: '#e3e7df', motif: 'frame',
    offers: [],
  };
}

export async function searchDiscoveryCreatorAccounts(query: string, offset = 0): Promise<{ creators: DiscoveryCreator[]; hasMore: boolean }> {
  const { data, error } = await supabase.rpc('search_discovery_creators', {
    p_query: query.trim(), p_limit: DISCOVERY_CREATOR_PAGE_SIZE + 1, p_offset: offset,
  });
  assertQuery(error, 'Could not search creator accounts');
  const profiles = (data || []) as ProfileRow[];
  return {
    creators: profiles.slice(0, DISCOVERY_CREATOR_PAGE_SIZE).map((profile) => creatorFromProfile(profile)),
    hasMore: profiles.length > DISCOVERY_CREATOR_PAGE_SIZE,
  };
}

export async function fetchDiscoveryContent(stage: DiscoveryStage): Promise<DiscoveryContent> {
  const [entryResult, settingsResult] = await Promise.all([
    supabase.from('discovery_entries').select(entryColumns).eq('stage', stage).order('sort_order'),
    supabase.from('discovery_page_settings').select('published_at,draft_revision').eq('stage', stage).single(),
  ]);
  assertQuery(entryResult.error, 'Could not load Discovery selections');
  assertQuery(settingsResult.error, 'Could not load Discovery page content');
  const entries = (entryResult.data || []) as DiscoveryEntry[];

  if (!entries.length) return {
    creators: [], selections: [], entries: [],
    publishedAt: settingsResult.data.published_at,
    revision: settingsResult.data.draft_revision,
  };
  const profileIds = [...new Set(entries.map((entry) => entry.profile_id))];
  const profileResult = await supabase.from('profiles').select(profileColumns)
    .eq('role', 'creator').in('id', profileIds).order('created_at', { ascending: false });
  assertQuery(profileResult.error, 'Could not load creators');
  const profiles = (profileResult.data || []) as ProfileRow[];
  if (!profiles.length) return {
    creators: [], selections: [], entries,
    publishedAt: settingsResult.data.published_at,
    revision: settingsResult.data.draft_revision,
  };

  const selections: DiscoverySelection[] = profiles.map((profile, index) => {
    const entry = entries.find((row) => row.profile_id === profile.id);
    return {
      creatorId: profile.id, status: entry?.status ?? 'pending',
      featured: entry?.featured ?? false, order: entry?.sort_order ?? index + 100,
      offerId: entry?.offer_id ?? '',
    };
  });
  const creators = profiles.map((profile) => creatorFromProfile(profile, entries.find((row) => row.profile_id === profile.id)));
  return {
    creators, selections, entries,
    publishedAt: settingsResult.data.published_at,
    revision: settingsResult.data.draft_revision,
  };
}

export async function canEditDiscovery(): Promise<{ allowed: boolean; admin: boolean }> {
  const { data: { user }, error: authError } = await supabase.auth.getUser();
  if (authError || !user) return { allowed: false, admin: false };
  const [profile, permission] = await Promise.all([
    supabase.from('profiles').select('role').eq('id', user.id).single(),
    supabase.from('discovery_editor_permissions').select('user_id').eq('user_id', user.id).maybeSingle(),
  ]);
  const admin = profile.data?.role === 'admin';
  return { allowed: admin || !!permission.data, admin };
}

export async function saveDiscoveryDraft(entries: DiscoveryEntry[], expectedRevision: number): Promise<number> {
  const { data, error } = await supabase.rpc('save_discovery_draft', {
    p_changes: entries, p_expected_revision: expectedRevision,
  });
  assertQuery(error, 'Could not save Discovery draft');
  return data as number;
}

export async function publishDiscovery(expectedRevision: number): Promise<string> {
  const { data, error } = await supabase.rpc('publish_discovery', { p_expected_revision: expectedRevision });
  assertQuery(error, 'Could not publish Discovery');
  return data as string;
}

export async function discardDiscoveryImage(path: string): Promise<void> {
  const { data, error } = await supabase.from('discovery_entries')
    .select('profile_id').eq('editorial_image_path', path).limit(1);
  assertQuery(error, 'Could not check Discovery photo');
  if (data?.length) return;
  const result = await supabase.storage.from('discovery-images').remove([path]);
  assertQuery(result.error, 'Could not remove unused Discovery photo');
}

export async function cleanupDiscoveryImages(): Promise<void> {
  const { data, error } = await supabase.from('discovery_image_cleanup_queue').select('path').limit(100);
  assertQuery(error, 'Could not load unused Discovery photos');
  for (const { path } of data || []) {
    const result = await supabase.storage.from('discovery-images').remove([path]);
    if (result.error || !result.data?.length) continue;
    const acknowledgement = await supabase.from('discovery_image_cleanup_queue').delete().eq('path', path);
    assertQuery(acknowledgement.error, 'Could not finish photo cleanup');
  }
}
